import {
  AaveV3GeneralPublicResponse,
  AaveV3GetreserveConfigDataIndexes,
  AaveV3GetReservesIndexes,
  YLDRTypeGetReserveConfigurationData,
  YLDRTypeGetReserveDataIndexes,
} from '../../../types/lender/aave-v3-types'
import { AAVE_V3_EMODES } from './publicCallBuild'
import { reserveIndicesAreSane } from './reserveIndexSanity'
import { rayAccumulator } from '../accumulator'
import { AdditionalYields } from '../../../types'
import {
  BPS,
  computeUtilization,
  formatAaveRawApyToApr,
  parseRawAmount,
} from '../../../utils/parsing'
import { Lender } from '@1delta/lender-registry'
import {
  getAaveAssets,
  getAaveStyleProtocolTokenMap,
  LENDER_MODE_NO_MODE,
  toGenericPriceKey,
  toOracleKey,
} from '../../../assets'
import { GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { lenderShortName } from '../../../utils/marketName'
import { isAaveV32Type } from '@1delta/lender-registry'

export const getAaveV3ReservesDataConverter = (
  lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [(data: any[]) => AaveV3GeneralPublicResponse | undefined, number] => {
  if (isAaveV32Type(lender))
    return parseAave32(
      chainId,
      lender,
      prices,
      additionalYields,
      tokenList,
    ) as any
  switch (lender) {
    /** AAVE V3 style with rewards from state */
    case Lender.YLDR:
      return parseYLDRCall(
        chainId,
        lender,
        additionalYields,
        prices,
        tokenList,
      ) as any
    default: {
      const assetsToQuery = getAaveAssets(chainId, lender)
      const allModes = AAVE_V3_EMODES(chainId, lender)
      const stride = 7
      const expectedNumberOfCalls =
        assetsToQuery.length * stride + allModes.length + 1
      const aaveTokenMap = getAaveStyleProtocolTokenMap(chainId, lender)

      return [
        (data: any[]) => {
          if (data.length !== expectedNumberOfCalls) {
            return undefined
          }
          const emodeDataResult = data.slice(
            assetsToQuery.length * stride,
            assetsToQuery.length * stride + allModes.length,
          )
          const resultReserves: any = {}
          const decimalsCeiling = data[expectedNumberOfCalls - 1]

          let eModeConfigs: any = {}
          allModes.forEach((mode, index) => {
            const rawCfg = emodeDataResult[index]
            if (
              (mode === 0 || rawCfg?.label !== '') &&
              Number(rawCfg.ltv) !== 0
            ) {
              eModeConfigs[mode] = {
                category: mode,
                label: rawCfg.label,
                borrowCollateralFactor: rawCfg.ltv / BPS,
                collateralFactor: rawCfg.liquidationThreshold / BPS,
                borrowFactor: 1,
                liquidationBonus: rawCfg.liquidationBonus,
              }
            }
          })

          for (let i = 0; i < assetsToQuery.length; i++) {
            const asset = assetsToQuery[i]

            const reserveData = data[i * stride]
            const configData = data[i * stride + 1]
            /** Unused so far */
            // const siloedBorrowing = data[i * stride + 2]
            const reserveCaps = data[i * stride + 3]
            const debtCeiling = data[i * stride + 4]

            const reserveEMode = data[i * stride + 5]
            const aTokenBalanceRaw = data[i * stride + 6]

            // Every balance below is derived from the reserve's RAY
            // accumulators. If those have been manipulated, the numbers are
            // fiction — skip the market rather than publish it.
            if (
              !reserveIndicesAreSane({
                chainId,
                lender,
                asset,
                liquidityIndex:
                  reserveData?.[AaveV3GetReservesIndexes.liquidityIndex],
                variableBorrowIndex:
                  reserveData?.[AaveV3GetReservesIndexes.variableBorrowIndex],
              })
            )
              continue

            const assetMeta = tokenList[asset]

            const decimals = assetMeta?.decimals ?? 18

            const totalDeposits = parseRawAmount(
              reserveData?.[AaveV3GetReservesIndexes.totalAToken]?.toString(),
              decimals,
            )
            const totalDebtStable = parseRawAmount(
              reserveData?.[
                AaveV3GetReservesIndexes.totalStableDebt
              ]?.toString(),
              decimals,
            )
            const totalDebt = parseRawAmount(
              reserveData?.[
                AaveV3GetReservesIndexes.totalVariableDebt
              ]?.toString(),
              decimals,
            )
            // Physical underlying balance held by the aToken — the actual
            // liquidity available for withdraw/borrow. Falls back to the
            // accrual-based estimate (clamped to 0) only if the balanceOf
            // call failed or the aToken address was missing.
            const aTokenHasAddress = !!aaveTokenMap[asset]?.aToken
            const availableLiquidityRaw =
              aTokenHasAddress && aTokenBalanceRaw !== '0x'
                ? aTokenBalanceRaw
                : undefined
            const liquidity =
              availableLiquidityRaw !== undefined
                ? Number(
                    parseRawAmount(availableLiquidityRaw?.toString(), decimals),
                  )
                : Math.max(
                    0,
                    Number(totalDeposits) -
                      Number(totalDebt) -
                      Number(totalDebtStable),
                  )
            const price =
              prices[
                toOracleKey(assetMeta?.assetGroup) ??
                  toGenericPriceKey(asset, chainId)
              ] ?? 0

            const usageAsCollateralEnabled =
              configData?.[
                AaveV3GetreserveConfigDataIndexes.usageAsCollateralEnabled
              ]
            const borrowingEnabled =
              configData?.[AaveV3GetreserveConfigDataIndexes.borrowingEnabled]

            const marketUid = createMarketUid(chainId, lender, asset)

            resultReserves[marketUid] = {
              marketUid,
              name: lenderShortName(lender) + ' ' + (assetMeta?.symbol ?? ''),
              poolId: asset,
              underlying: asset,
              asset: assetMeta,
              // raw amounts
              totalDeposits,
              totalDebtStable,
              totalDebt,
              totalLiquidity: liquidity,
              borrowLiquidity: liquidity,
              // USD amounts
              totalDepositsUSD: Number(totalDeposits) * price,
              totalDebtStableUSD: Number(totalDebtStable) * price,
              totalDebtUSD: Number(totalDebt) * price,
              totalLiquidityUSD: liquidity * price,
              borrowLiquidityUSD: liquidity * price,
              utilization: computeUtilization(
                totalDeposits,
                totalDebt,
                totalDebtStable,
              ),
              // the RAY accumulators, already read for the sanity gate above
              accumulator: rayAccumulator(
                reserveData?.[AaveV3GetReservesIndexes.liquidityIndex],
                reserveData?.[AaveV3GetReservesIndexes.variableBorrowIndex],
              ),
              // rates
              depositRate: formatAaveRawApyToApr(
                reserveData?.[
                  AaveV3GetReservesIndexes.liquidityRate
                ]?.toString(),
              ),
              variableBorrowRate: formatAaveRawApyToApr(
                reserveData?.[
                  AaveV3GetReservesIndexes.variableBorrowRate
                ]?.toString(),
              ),
              stableBorrowRate: formatAaveRawApyToApr(
                reserveData?.[
                  AaveV3GetReservesIndexes.stableBorrowRate
                ]?.toString(),
              ),
              intrinsicYield:
                additionalYields?.intrinsicYields?.[
                  toOracleKey(assetMeta?.assetGroup)
                ] ?? 0,
              // rewards
              rewards: [],
            }
            const eModeCategory = Number(reserveEMode?.toString())
            const baseLiquidationBonus = Number(
              configData?.[
                AaveV3GetreserveConfigDataIndexes.liquidationBonus
              ]?.toString(),
            )
            resultReserves[marketUid] = {
              ...resultReserves[marketUid],
              decimals: Number(
                configData?.[AaveV3GetreserveConfigDataIndexes.decimals],
              ),

              closeFactor: 0.5,

              config: populateEModes(
                Number(configData?.[AaveV3GetreserveConfigDataIndexes.ltv]) /
                  BPS,
                Number(
                  configData?.[
                    AaveV3GetreserveConfigDataIndexes.liquidationThreshold
                  ],
                ) / BPS,
                eModeCategory,
                eModeConfigs,
                allModes,
                usageAsCollateralEnabled,
                borrowingEnabled,
                baseLiquidationBonus,
              ),

              // flags
              collateralActive: usageAsCollateralEnabled,
              borrowingEnabled: borrowingEnabled,
              depositsEnabled: true,
              hasStable:
                configData?.[
                  AaveV3GetreserveConfigDataIndexes.stableBorrowRateEnabled
                ],
              isActive:
                configData?.[AaveV3GetreserveConfigDataIndexes.isActive],
              isFrozen:
                configData?.[AaveV3GetreserveConfigDataIndexes.isFrozen],

              // caps
              borrowCap: Number(reserveCaps[0]?.toString()),
              supplyCap: Number(reserveCaps[1]?.toString()),
              debtCeiling: Number(
                parseRawAmount(
                  debtCeiling?.toString(),
                  Number(decimalsCeiling?.toString()),
                ),
              ),
              // borrow liquidity (cap-adjusted)
              ...(() => {
                const bc = Number(reserveCaps[0]?.toString())
                const liq = resultReserves[marketUid].totalLiquidity
                const bl =
                  bc > 0
                    ? Math.min(
                        liq,
                        Math.max(
                          0,
                          bc - Number(totalDebt) - Number(totalDebtStable),
                        ),
                      )
                    : liq
                return {
                  borrowLiquidity: bl,
                  borrowLiquidityUSD: bl * price,
                }
              })(),
            }

            const aaveTokens = aaveTokenMap[asset]

            if (!!aaveTokens) {
              // metadata
              resultReserves[marketUid] = {
                ...resultReserves[marketUid],
                params: {
                  metadata: {
                    aToken: aaveTokens.aToken,
                    vToken: aaveTokens.vToken,
                    sToken: aaveTokens.sToken,
                  },
                },
              }
            }
          }
          return {
            data: resultReserves,
            chainId,
          }
        },
        expectedNumberOfCalls,
      ]
    }
  }
}

/**
 * Convert an Aave-style `liquidationBonus` (BPS, where 10500 == 105% payout)
 * into a liquidation penalty fraction (bonus over par, e.g. 0.05). Guards
 * against NaN/undefined/sub-par values → 0.
 */
const toLiquidationPenalty = (liquidationBonus: number): number =>
  Number.isFinite(liquidationBonus) && liquidationBonus > 0
    ? liquidationBonus / BPS - 1
    : 0

const populateEModes = (
  borrowCollateralFactor: number,
  collateralFactor: number,
  eModeCategory: number,
  eModeConfigs: any,
  allModes: any[],
  usageAsCollateralEnabled: boolean,
  borrowingEnabled: boolean,
  baseLiquidationBonus: number,
) => {
  const basePenalty = toLiquidationPenalty(baseLiquidationBonus)
  let data: any = {}
  allModes.forEach((e) => {
    if (e === 0)
      data[e] = {
        label: 'Disabled',
        category: e,
        borrowCollateralFactor,
        collateralFactor,
        borrowFactor: 1,
        liquidationPenalty: basePenalty,
        closeFactor: 0.5,
        collateralDisabled: !usageAsCollateralEnabled,
        debtDisabled: !borrowingEnabled,
      }
    if (e > 0 && eModeConfigs[e]?.label && eModeConfigs[e]?.label !== '') {
      if (e === eModeCategory) {
        // asset belongs to this e-mode: use e-mode params and global flags
        data[e] = {
          label: eModeConfigs[e]?.label,
          category: e,
          borrowCollateralFactor: eModeConfigs[e]?.borrowCollateralFactor ?? 0,
          collateralFactor: eModeConfigs[e]?.collateralFactor ?? 0,
          borrowFactor: 1,
          liquidationPenalty: toLiquidationPenalty(
            eModeConfigs[e]?.liquidationBonus,
          ),
          closeFactor: 0.5,
          collateralDisabled: !usageAsCollateralEnabled,
          debtDisabled: !borrowingEnabled,
        }
      } else {
        // asset does not belong to this e-mode: disable both
        data[e] = {
          label: eModeConfigs[e]?.label,
          category: e,
          borrowCollateralFactor,
          collateralFactor,
          borrowFactor: 1,
          liquidationPenalty: basePenalty,
          closeFactor: 0.5,
          collateralDisabled: false,
          debtDisabled: true,
        }
      }
    }
  })
  return data
}

const populateEModes32 = (
  borrowCollateralFactor: number,
  collateralFactor: number,
  collateralBitmap: any,
  debtBitmap: any,
  assetIndex: number,
  eModeConfigs: any,
  allModes: any[],
  usageAsCollateralEnabled: boolean,
  borrowingEnabled: boolean,
  baseLiquidationBonus: number,
) => {
  const basePenalty = toLiquidationPenalty(baseLiquidationBonus)
  let data: any = {}
  allModes.forEach((e) => {
    if (e === 0)
      data[e] = {
        label: 'Disabled',
        category: e,
        borrowCollateralFactor,
        collateralFactor,
        borrowFactor: 1,
        liquidationPenalty: basePenalty,
        closeFactor: 0.5,
        collateralDisabled: !usageAsCollateralEnabled,
        debtDisabled: !borrowingEnabled,
      }
    if (e > 0) {
      // only add if configs defined
      if (eModeConfigs[e]?.label && eModeConfigs[e]?.label !== '') {
        data[e] = {}
        // add base data
        data[e].label = eModeConfigs[e]?.label ?? 'Default'
        data[e].debtDisabled = true
        data[e].category = e
        data[e].borrowFactor = 1
        data[e].collateralFactor = collateralFactor
        data[e].borrowCollateralFactor = borrowCollateralFactor
        data[e].liquidationPenalty = basePenalty
        data[e].closeFactor = 0.5
        data[e].debtDisabled = !!eModeConfigs[e]
        // override info if mode explicitly provided
        if (eModeConfigs[e]) {
          data[e].category = e
          data[e].borrowCollateralFactor =
            eModeConfigs[e]?.borrowCollateralFactor ?? 0
          data[e].collateralFactor = eModeConfigs[e]?.collateralFactor ?? 0
          data[e].borrowFactor = 1
          data[e].liquidationPenalty = toLiquidationPenalty(
            eModeConfigs[e]?.liquidationBonus,
          )
          data[e].closeFactor = 0.5
          data[e].collateralDisabled = !isReserveEnabledOnBitmap(
            collateralBitmap[e],
            assetIndex,
          )
          data[e].debtDisabled = !isReserveEnabledOnBitmap(
            debtBitmap[e],
            assetIndex,
          )
        }
      }
    }
  })
  return data
}

/** Replica of aave's bitmap checker */
function isReserveEnabledOnBitmap(
  bitmap: bigint,
  reserveIndex: number,
): boolean {
  try {
    return ((bitmap >> BigInt(reserveIndex)) & 1n) !== 0n
  } catch {
    return false
  }
}

export function parseYLDRCall(
  chainId: string,
  lender: string,
  additionalYields: any,
  prices: any,
  tokenList: any,
): [(data: any[]) => any | undefined, number] {
  const assetsToQuery = getAaveAssets(chainId, lender)
  const aaveTokenMap = getAaveStyleProtocolTokenMap(chainId, lender)

  const stride = 3
  const expectedNumberOfCalls = assetsToQuery.length * stride

  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) {
        return undefined
      }
      const resultReserves: any = {}

      for (let i = 0; i < assetsToQuery.length; i++) {
        const asset = assetsToQuery[i]
        const reserveData = data[i * stride]
        const configData = data[i * stride + 1]
        const aTokenBalanceRaw = data[i * stride + 2]

        const assetMeta = tokenList[asset]
        const decimals = assetMeta?.decimals ?? 18

        const totalVariableDebt = parseRawAmount(
          reserveData?.[
            YLDRTypeGetReserveDataIndexes.totalVariableDebt
          ]?.toString(),
          decimals,
        )
        const totalAToken = parseRawAmount(
          reserveData?.[YLDRTypeGetReserveDataIndexes.totalYToken]?.toString(),
          decimals,
        )
        const aTokenHasAddress = !!aaveTokenMap[asset]?.aToken
        const availableLiquidityRaw =
          aTokenHasAddress && aTokenBalanceRaw !== '0x'
            ? aTokenBalanceRaw
            : undefined
        const liquidity =
          availableLiquidityRaw !== undefined
            ? Number(
                parseRawAmount(availableLiquidityRaw?.toString(), decimals),
              )
            : Math.max(0, Number(totalAToken) - Number(totalVariableDebt))

        const oracleKey =
          toOracleKey(assetMeta?.assetGroup) ??
          toGenericPriceKey(asset, chainId)
        const price = prices[oracleKey] ?? 0
        const totalDepositsUSD = Number(totalAToken) * price
        const totalDebtUSD = Number(totalVariableDebt) * price

        const usageAsCollateralEnabled =
          configData?.[
            YLDRTypeGetReserveConfigurationData.usageAsCollateralEnabled
          ]
        const borrowingEnabled =
          configData?.[YLDRTypeGetReserveConfigurationData.borrowingEnabled]

        const marketUid = createMarketUid(chainId, lender, asset)

        resultReserves[marketUid] = {
          marketUid,
          name: lenderShortName(lender) + ' ' + (assetMeta?.symbol ?? ''),
          poolId: asset,
          underlying: asset,
          asset: assetMeta,
          // token amounts
          totalDeposits: totalAToken,
          totalDebtStable: 0,
          totalDebt: totalVariableDebt,
          totalLiquidity: liquidity,
          borrowLiquidity: liquidity,
          // USD amounts
          totalDepositsUSD,
          totalDebtStableUSD: 0,
          totalDebtUSD,
          totalLiquidityUSD: liquidity * price,
          borrowLiquidityUSD: liquidity * price,
          utilization: computeUtilization(totalAToken, totalVariableDebt),
          accumulator: rayAccumulator(
            reserveData?.[YLDRTypeGetReserveDataIndexes.liquidityIndex],
            reserveData?.[YLDRTypeGetReserveDataIndexes.variableBorrowIndex],
          ),
          // rates
          depositRate: formatAaveRawApyToApr(
            reserveData?.[
              YLDRTypeGetReserveDataIndexes.liquidityRate
            ]?.toString(),
          ),
          variableBorrowRate: formatAaveRawApyToApr(
            reserveData?.[
              YLDRTypeGetReserveDataIndexes.variableBorrowRate
            ]?.toString(),
          ),
          stableBorrowRate: 0,
          intrinsicYield: additionalYields?.intrinsicYields?.[oracleKey] ?? 0,

          // rewards
          rewards: [],
          decimals: Number(
            configData?.[YLDRTypeGetReserveConfigurationData.decimals],
          ),
          config: {
            [LENDER_MODE_NO_MODE]: {
              category: LENDER_MODE_NO_MODE,
              // collateral factors
              borrowCollateralFactor:
                Number(
                  configData?.[
                    YLDRTypeGetReserveConfigurationData.ltv
                  ]?.toString(),
                ) / BPS,
              collateralFactor:
                Number(
                  configData?.[
                    YLDRTypeGetReserveConfigurationData.liquidationThreshold
                  ]?.toString(),
                ) / BPS,
              borrowFactor: 1,
              liquidationPenalty: toLiquidationPenalty(
                Number(
                  configData?.[
                    YLDRTypeGetReserveConfigurationData.liquidationBonus
                  ]?.toString(),
                ),
              ),
              closeFactor: 0.5,
              collateralDisabled: !usageAsCollateralEnabled,
              debtDisabled: !borrowingEnabled,
            },
          },
          closeFactor: 0.5,
          liquidationBonus:
            Number(
              configData?.[
                YLDRTypeGetReserveConfigurationData.liquidationBonus
              ]?.toString(),
            ) / BPS,
          // flags
          collateralActive: usageAsCollateralEnabled,
          borrowingEnabled: borrowingEnabled,
          depositsEnabled: true,
          hasStable: false,
          isActive: configData?.[YLDRTypeGetReserveConfigurationData.isActive],
          isFrozen: configData?.[YLDRTypeGetReserveConfigurationData.isFrozen],
        }

        const aaveTokens = aaveTokenMap[asset]

        if (!!aaveTokens) {
          // metadata
          resultReserves[marketUid] = {
            ...resultReserves[marketUid],
            params: {
              metadata: {
                aToken: aaveTokens.aToken,
                vToken: aaveTokens.vToken,
                sToken: aaveTokens.sToken,
              },
            },
          }
        }
      }

      return {
        data: resultReserves,
        chainId,
      }
    },
    expectedNumberOfCalls,
  ]
}

function parseAave32(
  chainId: string,
  lender: string,
  prices: any,
  additionalYields: any,
  tokenList: any,
) {
  const assetsToQuery = getAaveAssets(chainId, lender)
  const aaveTokenMap = getAaveStyleProtocolTokenMap(chainId, lender)

  const allModes = AAVE_V3_EMODES(chainId, lender)
  const stride = 6
  const expectedNumberOfCalls =
    assetsToQuery.length * stride + allModes.length * 3 + 2

  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) {
        return undefined
      }

      const emodeDataResult = data.slice(
        assetsToQuery.length * stride,
        assetsToQuery.length * stride + allModes.length * 3,
      )
      const resultReserves: any = {}

      const reservesList = data[expectedNumberOfCalls - 2]
      const decimalsCeiling = data[expectedNumberOfCalls - 1]

      let eModeConfigs: any = {}
      let collateralBitmap: any = {}
      let debtBitmap: any = {}
      allModes.forEach((mode, index) => {
        const rawCfg = emodeDataResult[3 * index]
        if (rawCfg && (mode === 0 || rawCfg?.label !== '')) {
          eModeConfigs[mode] = {
            category: mode,
            label: rawCfg.label,
            borrowCollateralFactor: rawCfg.ltv / BPS,
            collateralFactor: rawCfg.liquidationThreshold / BPS,
            borrowFactor: 1,
            liquidationBonus: rawCfg.liquidationBonus,
          }
          debtBitmap[mode] = emodeDataResult[3 * index + 1]
          collateralBitmap[mode] = emodeDataResult[3 * index + 2]
        }
      })

      // we get the reserves list to obtain the index per reserve
      const lowerReservesList = (reservesList as string[])?.map((a) =>
        a.toLowerCase(),
      )

      for (let i = 0; i < assetsToQuery.length; i++) {
        const asset = assetsToQuery[i]

        const reserveData = data[i * stride]
        const configData = data[i * stride + 1]
        /** Unused so far */
        // const siloedBorrowing = data[i * stride + 2]
        const reserveCaps = data[i * stride + 3]
        const debtCeiling = data[i * stride + 4]
        const aTokenBalanceRaw = data[i * stride + 5]

        // Same accumulator guard as the v3.0 path — see reserveIndexSanity.ts.
        if (
          !reserveIndicesAreSane({
            chainId,
            lender,
            asset,
            liquidityIndex:
              reserveData?.[AaveV3GetReservesIndexes.liquidityIndex],
            variableBorrowIndex:
              reserveData?.[AaveV3GetReservesIndexes.variableBorrowIndex],
          })
        )
          continue

        const assetMeta = tokenList[asset]

        const decimals = assetMeta?.decimals ?? 18
        const totalDeposits = parseRawAmount(
          reserveData?.[AaveV3GetReservesIndexes.totalAToken]?.toString(),
          decimals,
        )
        const totalDebtStable = parseRawAmount(
          reserveData?.[AaveV3GetReservesIndexes.totalStableDebt]?.toString(),
          decimals,
        )
        const totalDebt = parseRawAmount(
          reserveData?.[AaveV3GetReservesIndexes.totalVariableDebt]?.toString(),
          decimals,
        )
        const aTokenHasAddress = !!aaveTokenMap[asset]?.aToken
        const availableLiquidityRaw =
          aTokenHasAddress && aTokenBalanceRaw !== '0x'
            ? aTokenBalanceRaw
            : undefined
        const liquidity =
          availableLiquidityRaw !== undefined
            ? Number(
                parseRawAmount(availableLiquidityRaw?.toString(), decimals),
              )
            : Math.max(
                0,
                Number(totalDeposits) -
                  Number(totalDebt) -
                  Number(totalDebtStable),
              )
        const price =
          prices[
            toOracleKey(assetMeta?.assetGroup) ??
              toGenericPriceKey(asset, chainId)
          ] ?? 0
        const marketUid = createMarketUid(chainId, lender, asset)

        resultReserves[marketUid] = {
          marketUid,
          name: lenderShortName(lender) + ' ' + (assetMeta?.symbol ?? ''),
          poolId: asset,
          underlying: asset,
          asset: assetMeta,
          // raw amounts
          totalDeposits,
          totalDebtStable,
          totalDebt,
          totalLiquidity: liquidity,
          borrowLiquidity: liquidity,
          // USD amounts
          totalDepositsUSD: Number(totalDeposits) * price,
          totalDebtStableUSD: Number(totalDebtStable) * price,
          totalDebtUSD: Number(totalDebt) * price,
          totalLiquidityUSD: liquidity * price,
          borrowLiquidityUSD: liquidity * price,
          utilization: computeUtilization(
            totalDeposits,
            totalDebt,
            totalDebtStable,
          ),
          accumulator: rayAccumulator(
            reserveData?.[AaveV3GetReservesIndexes.liquidityIndex],
            reserveData?.[AaveV3GetReservesIndexes.variableBorrowIndex],
          ),
          // rates
          depositRate: formatAaveRawApyToApr(
            reserveData?.[AaveV3GetReservesIndexes.liquidityRate]?.toString(),
          ),
          variableBorrowRate: formatAaveRawApyToApr(
            reserveData?.[
              AaveV3GetReservesIndexes.variableBorrowRate
            ]?.toString(),
          ),
          stableBorrowRate: formatAaveRawApyToApr(
            reserveData?.[
              AaveV3GetReservesIndexes.stableBorrowRate
            ]?.toString(),
          ),

          intrinsicYield:
            additionalYields?.intrinsicYields?.[
              toOracleKey(assetMeta?.assetGroup)
            ] ?? 0,
          // rewards
          rewards: [],
        }
        const assetIndex = lowerReservesList.indexOf(asset)

        const usageAsCollateralEnabled =
          configData?.[
            AaveV3GetreserveConfigDataIndexes.usageAsCollateralEnabled
          ]
        const borrowingEnabled =
          configData?.[AaveV3GetreserveConfigDataIndexes.borrowingEnabled]
        const baseLiquidationBonus = Number(
          configData?.[
            AaveV3GetreserveConfigDataIndexes.liquidationBonus
          ]?.toString(),
        )

        resultReserves[marketUid] = {
          ...resultReserves[marketUid],
          decimals: Number(
            configData?.[AaveV3GetreserveConfigDataIndexes.decimals],
          ),

          closeFactor: 0.5,

          config: populateEModes32(
            Number(configData?.[AaveV3GetreserveConfigDataIndexes.ltv]) / BPS,
            Number(
              configData?.[
                AaveV3GetreserveConfigDataIndexes.liquidationThreshold
              ],
            ) / BPS,
            collateralBitmap,
            debtBitmap,
            assetIndex,
            eModeConfigs,
            allModes,
            usageAsCollateralEnabled,
            borrowingEnabled,
            baseLiquidationBonus,
          ),

          // flags
          collateralActive: usageAsCollateralEnabled,
          borrowingEnabled: borrowingEnabled,
          depositsEnabled: true,
          hasStable:
            configData?.[
              AaveV3GetreserveConfigDataIndexes.stableBorrowRateEnabled
            ],
          isActive: configData?.[AaveV3GetreserveConfigDataIndexes.isActive],
          isFrozen: configData?.[AaveV3GetreserveConfigDataIndexes.isFrozen],

          // caps
          borrowCap: Number(reserveCaps[0]?.toString()),
          supplyCap: Number(reserveCaps[1]?.toString()),
          debtCeiling: Number(
            parseRawAmount(
              debtCeiling?.toString(),
              Number(decimalsCeiling?.toString()),
            ),
          ),
          // borrow liquidity (cap-adjusted)
          ...(() => {
            const bc = Number(reserveCaps[0]?.toString())
            const liq = resultReserves[marketUid].totalLiquidity
            const bl =
              bc > 0
                ? Math.min(
                    liq,
                    Math.max(
                      0,
                      bc - Number(totalDebt) - Number(totalDebtStable),
                    ),
                  )
                : liq
            return {
              borrowLiquidity: bl,
              borrowLiquidityUSD: bl * price,
            }
          })(),
        }

        const aaveTokens = aaveTokenMap[asset]

        if (!!aaveTokens) {
          // metadata
          resultReserves[marketUid] = {
            ...resultReserves[marketUid],
            params: {
              metadata: {
                aToken: aaveTokens.aToken,
                vToken: aaveTokens.vToken,
                sToken: aaveTokens.sToken,
              },
            },
          }
        }
      }
      return {
        data: resultReserves,
        chainId,
      }
    },
    expectedNumberOfCalls,
  ]
}
